import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api/api";

// Récupérer tous les artisans
export const get_artisans = createAsyncThunk(
    'artisan/get_artisans',
    async({ search, status }, { rejectWithValue, fulfillWithValue }) => {
        try {
            let url = `/admin/artisans?`;
            if (search) url += `search=${search}&`;
            if (status) url += `status=${status}`;
            
            const { data } = await api.get(url, { withCredentials: true });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Récupérer un artisan spécifique
export const get_artisan_details = createAsyncThunk(
    'artisan/get_artisan_details',
    async(id, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get(`/admin/artisans/${id}`, { withCredentials: true });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Récupérer les vendeurs disponibles
export const get_available_sellers = createAsyncThunk(
    'artisan/get_available_sellers',
    async(_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get('/admin/artisans/available-sellers', { withCredentials: true });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Définir un vendeur comme artisan du mois
export const set_artisan_du_mois = createAsyncThunk(
    'artisan/set_artisan_du_mois',
    async({ sellerId, category, description, video, rating, image }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('sellerId', sellerId);
            formData.append('category', category);
            formData.append('description', description);
            formData.append('video', video || '');
            formData.append('rating', rating || 0);
            if (image) {
                formData.append('image', image);
            }

            const { data } = await api.post('/admin/artisans', formData, { 
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Mettre à jour un artisan
export const update_artisan = createAsyncThunk(
    'artisan/update_artisan',
    async({ id, category, description, video, rating, image }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('category', category);
            formData.append('description', description);
            formData.append('video', video || '');
            formData.append('rating', rating || 0);
            if (image) {
                formData.append('image', image);
            }

            const { data } = await api.put(`/admin/artisans/${id}`, formData, { 
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Retirer le statut d'artisan du mois
export const remove_artisan = createAsyncThunk(
    'artisan/remove_artisan',
    async(id, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.delete(`/admin/artisans/${id}`, { withCredentials: true });
            return fulfillWithValue({ ...data, id });
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Activer/Désactiver un artisan
export const toggle_artisan_status = createAsyncThunk(
    'artisan/toggle_artisan_status',
    async(id, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.patch(`/admin/artisans/${id}/toggle`, {}, { withCredentials: true });
            return fulfillWithValue({ ...data, id });
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Récupérer les statistiques
export const get_artisans_stats = createAsyncThunk(
    'artisan/get_artisans_stats',
    async(_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get('/admin/artisans/stats', { withCredentials: true });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const artisanReducer = createSlice({
    name: 'artisan',
    initialState: {
        successMessage: '',
        errorMessage: '',
        loader: false,
        artisans: [],
        artisanDetails: null,
        availableSellers: [],
        stats: {
            total: 0,
            active: 0,
            inactive: 0
        }
    },
    reducers: {
        messageClear: (state, _) => {
            state.errorMessage = "";
            state.successMessage = "";
        }
    },
    extraReducers: (builder) => {
        builder
            // Get artisans
            .addCase(get_artisans.pending, (state) => {
                state.loader = true;
            })
            .addCase(get_artisans.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.artisans = payload.artisans;
            })
            .addCase(get_artisans.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.error || 'Erreur lors du chargement';
            })

            // Get artisan details
            .addCase(get_artisan_details.pending, (state) => {
                state.loader = true;
            })
            .addCase(get_artisan_details.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.artisanDetails = payload.artisan;
            })
            .addCase(get_artisan_details.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.error || 'Erreur lors du chargement';
            })

            // Get available sellers
            .addCase(get_available_sellers.fulfilled, (state, { payload }) => {
                state.availableSellers = payload.sellers;
            })

            // Set artisan du mois
            .addCase(set_artisan_du_mois.pending, (state) => {
                state.loader = true;
            })
            .addCase(set_artisan_du_mois.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.successMessage = payload.message;
                state.artisans = [...state.artisans, payload.artisan];
            })
            .addCase(set_artisan_du_mois.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.error || 'Erreur lors de la création';
            })

            // Update artisan
            .addCase(update_artisan.pending, (state) => {
                state.loader = true;
            })
            .addCase(update_artisan.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.successMessage = payload.message;
                const index = state.artisans.findIndex(a => a._id === payload.artisan._id);
                if (index !== -1) {
                    state.artisans[index] = payload.artisan;
                }
            })
            .addCase(update_artisan.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.error || 'Erreur lors de la mise à jour';
            })

            // Remove artisan
            .addCase(remove_artisan.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message;
                state.artisans = state.artisans.filter(a => a._id !== payload.id);
            })
            .addCase(remove_artisan.rejected, (state, { payload }) => {
                state.errorMessage = payload?.error || 'Erreur lors de la suppression';
            })

            // Toggle status
            .addCase(toggle_artisan_status.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message;
                const index = state.artisans.findIndex(a => a._id === payload.id);
                if (index !== -1) {
                    state.artisans[index].status = payload.status;
                }
            })
            .addCase(toggle_artisan_status.rejected, (state, { payload }) => {
                state.errorMessage = payload?.error || 'Erreur lors du changement de statut';
            })

            // Get stats
            .addCase(get_artisans_stats.fulfilled, (state, { payload }) => {
                state.stats = payload.stats;
            });
    }
});

export const { messageClear } = artisanReducer.actions;
export default artisanReducer.reducer;
